import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  exposureDetailQuery,
  exposureQuery,
  machineHref,
  type ExposureHost,
  type ExposurePort,
  type ExposureSource,
} from '../../api/admExposure'
import { admDashboardQuery } from '../../api/admDashboard'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { Modal } from '../../ui/Modal'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField } from './AdmFilterBar'
import { AdmViews } from './AdmViews'

/**
 * ADM -> Exposure, din `views/version-3/softsee/adm/exposure/`.
 *
 * A CINCEA VEDERE: ce asculta in retea, din doua surse - ce au raportat
 * agentii intre scanari, si ce a gasit deschis scanarea releului pe aparatele
 * descoperite. Fiecare rand spune de unde se stie (coloana Source); o retea fara
 * agenti nu mai arata "nimic deschis" cand releul a gasit RDP si SMB.
 *
 * TREI RASPUNSURI LA "RUNS AS", nu doua. "Nu se stie" nu e acelasi lucru cu
 * "nu e serviciu": agentii de pana acum nu trimit campul deloc, iar un "no" pe
 * randurile lor ar fi o afirmatie pe care n-a facut-o nimeni.
 */

const STYLES = ['css/adm.css']

const SOURCE_LABEL: Record<ExposureSource, string> = { agent: 'Agent', scan: 'Scan' }

/** De unde se stie: agentul de pe masina, sau scanarea releului din retea. */
function SourcePill({ source }: { source: ExposureSource }) {
  return (
    <span
      className={source === 'scan' ? 'adm-pill adm-pill-running expSourcePill' : 'adm-pill expSourcePill'}
      title={source === 'scan' ? 'Found open by a relay network scan' : 'Reported by the agent on the machine'}
    >
      {SOURCE_LABEL[source]}
    </span>
  )
}

/** Numele masinii, ca legatura spre fisa ei cand are una. */
function MachineLink({ host, withFqdn = false }: { host: ExposureHost; withFqdn?: boolean }) {
  const href = machineHref(host)
  const name = host.name || host.address || '—'
  /* Numele scurt; FQDN-ul (wks02.cecdemo.local) dedesubt in fereastra portului,
     si in titlu pe lista scurta de pe rand. */
  const fqdn = host.fqdn ? <div className="adm-muted" style={{ fontSize: 11 }}>{host.fqdn}</div> : null
  if (!href) {
    return (
      <>
        {name}
        {withFqdn ? fqdn : null}
      </>
    )
  }
  const scanned = host.discoveredId > 0 && host.deviceId === 0 && host.ciId === 0
  return (
    <>
      <a className="expHost" href={href} title={(scanned ? 'Found by a scan - open it in Discovered' : 'Open the machine') + (host.fqdn ? ` (${host.fqdn})` : '')}>
        {name}
      </a>
      {withFqdn ? fqdn : null}
    </>
  )
}

/**
 * PE CE MASINI e deschis portul, direct pe rand: „iti zice ca e portul
 * deschis, dar al cui e?". Primele cateva, cu legatura spre fisa fiecareia;
 * restul e „+N", si se vad toate in fereastra portului.
 */
function MachineNames({ row, onMore }: { row: ExposurePort; onMore: () => void }) {
  const hosts = row.hosts ?? []
  const more = Math.max(0, row.machines - hosts.length)
  if (hosts.length === 0) return null
  return (
    <span className="expHosts">
      {hosts.map((host, i) => (
        <span key={`${host.ciId}-${host.discoveredId}-${host.address}`}>
          {i > 0 ? ', ' : null}
          <MachineLink host={host} />
        </span>
      ))}
      {more > 0 ? (
        <>
          {' '}
          <a
            href="#"
            className="expMore"
            title="Show all the machines"
            onClick={(e) => {
              e.preventDefault()
              onMore()
            }}
          >
            +{more.toLocaleString('en-US')}
          </a>
        </>
      ) : null}
    </span>
  )
}

/** Serviciu, program pornit de om, sau tacere. */
function RunsAs({ row }: { row: ExposurePort }) {
  if (row.serviceKnown === 0) {
    return (
      <span className="adm-muted" style={{ fontSize: 11 }} title="No agent has reported this yet">
        —
      </span>
    )
  }
  if (row.asService === 0) return <span className="adm-pill">app</span>
  if (row.asService >= row.machines) return <span className="adm-pill adm-pill-ok">service</span>
  /* Amestecat: acelasi port, serviciu pe unele masini si program pe altele.
     Merita vazut - de-aia poarta si numarul, nu doar cuvantul. */
  return (
    <span className="adm-pill adm-pill-warn" title="Service on some machines, a user program on others">
      {row.asService}/{row.machines} service
    </span>
  )
}

export function ExposurePage() {
  const shell = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shell.data?.version ?? '')

  /* Banda de vederi vine de la acelasi capat ca pe tabloul de bord: lista e
     filtrata pe drepturi si pe ce s-a mutat, si se scrie o singura data. */
  const chrome = useQuery(admDashboardQuery(30, 0))

  const [port, setPort] = useState(0)
  const [portText, setPortText] = useState('')
  const [search, setSearch] = useState('')
  const [source, setSource] = useState<ExposureSource | ''>('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState<{ port: number; proto: number } | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(search.trim())
      setPort(Number(portText) > 0 ? Number(portText) : 0)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [search, portText])

  const page = useQuery({ ...exposureQuery(port, debounced, source), placeholderData: keepPreviousData })
  const detail = useQuery(exposureDetailQuery(open?.port ?? 0, open?.proto ?? 0, source))

  const data = page.data

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="yellow"
          icon="fa-ethernet"
          title="What is listening"
          note="Ports observed open across the estate: reported by agents, and found open by relay scans."
          stats={[
            { value: String(data?.ports ?? 0), label: 'port/service pairs', tone: 'blue-hoki' },
            { value: (data?.machines ?? 0).toLocaleString('en-US'), label: 'exposures in total', tone: 'grey' },
            { value: String(data?.watched ?? 0), label: 'worth a look', tone: (data?.watched ?? 0) > 0 ? 'yellow' : 'grey' },
          ]}
        />

        <AdmViews views={chrome.data?.views ?? []} current="exposure" />

        <AdmFilterBar>
          <AdmField label="Service, application or machine" htmlFor="expSearch" wide>
            <input
              type="text"
              className="form-control input-sm"
              id="expSearch"
              placeholder="ssh, w3wp.exe, DC01…"
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </AdmField>
          <AdmField label="Port" htmlFor="expPort">
            <input
              type="number"
              className="form-control input-sm"
              id="expPort"
              min={1}
              placeholder="any"
              value={portText}
              onChange={(e) => setPortText(e.target.value)}
            />
          </AdmField>
          <AdmField label="Source" htmlFor="expSource">
            <select
              className="form-control input-sm"
              id="expSource"
              value={source}
              onChange={(e) => setSource(e.target.value as ExposureSource | '')}
            >
              <option value="">Agent and scan</option>
              <option value="agent">Agent</option>
              <option value="scan">Scan</option>
            </select>
          </AdmField>
        </AdmFilterBar>

        {data && data.rows.length === 0 ? (
          <div className="adm-empty-inline">
            <i className="fas fa-ethernet"></i> Nothing has reported an open port yet.
          </div>
        ) : (
          <table className="table table-hover adm-list" id="admExposureTable" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 110 }} className="adm-numeric">
                  Port
                </th>
                <th>Service</th>
                <th>Application</th>
                <th style={{ width: 100 }}>Runs as</th>
                <th style={{ minWidth: 220 }}>Machines</th>
                <th style={{ width: 130 }} className="adm-numeric">
                  All interfaces
                </th>
                <th style={{ width: 130 }}>Last seen</th>
                <th style={{ width: 80 }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((row) => (
                <tr key={`${row.source}-${row.proto}-${row.port}-${row.serviceName}-${row.application}`}>
                  <td className="adm-numeric">
                    <strong>{row.port}</strong>{' '}
                    <span className="adm-muted" style={{ fontSize: 11 }}>
                      {row.protoName}
                    </span>
                  </td>
                  <td>
                    {row.serviceName || '—'}{' '}
                    {row.watched ? (
                      <span className="adm-pill adm-pill-warn" title="Administration or sharing port - worth a second look">
                        watch
                      </span>
                    ) : null}
                  </td>
                  <td className="adm-muted" style={{ fontSize: 12 }}>
                    {row.application || '—'}
                  </td>
                  <td>
                    <RunsAs row={row} />
                  </td>
                  <td>
                    {/* Buton, nu cifra simpla: altfel nu se vede ca deschide lista masinilor.
                        Ochiul spune ca e ceva de deschis; `expWho` din adm.css ii da
                        bordura si fundalul inapoi, pe care tabelul i le lua. */}
                    <button
                      type="button"
                      className="btn btn-default btn-xs expWho"
                      title="Show all the machines listening on this port"
                      onClick={() => setOpen({ port: row.port, proto: row.proto })}
                    >
                      <i className="fa fa-eye" aria-hidden="true"></i> {row.machines.toLocaleString('en-US')}
                    </button>{' '}
                    <MachineNames row={row} onMore={() => setOpen({ port: row.port, proto: row.proto })} />
                  </td>
                  <td className="adm-numeric">
                    {row.onAllInterfaces > 0 ? (
                      /* Deschis catre ORICE, nu doar catre segmentul lui - si
                         asta e chiar deosebirea pe care o cauta un audit. */
                      <span className="adm-pill adm-pill-warn">{row.onAllInterfaces.toLocaleString('en-US')}</span>
                    ) : (
                      <span className="adm-muted">—</span>
                    )}
                  </td>
                  <td className="adm-muted" style={{ fontSize: 11.5 }}>
                    {row.lastSeen}
                  </td>
                  <td>
                    <SourcePill source={row.source} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Plafonul se SPUNE, nu se tace: ce se vede e cel mai des intalnit, nu
            tot, si un ecran care tace despre asta e citit ca un inventar. */}
        {data?.reachedCeiling ? (
          <div className="adm-warning" id="expWarning">
            <i className="fas fa-info-circle"></i> Only the most widespread ports are shown. Narrow it with the filters
            above to see the rest.
          </div>
        ) : null}
      </div>

      <Modal
        id="expWhoModal"
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? `What is listening on ${open.port}` : ''}
        large
      >
        {detail.isFetching && !detail.data ? (
          <p className="adm-muted">Reading…</p>
        ) : (detail.data?.rows.length ?? 0) === 0 ? (
          <p className="adm-muted">Nothing is listening on this port right now.</p>
        ) : (
          <table className="table adm-list">
            <thead>
              <tr>
                <th>Machine</th>
                <th>Service</th>
                <th>Address</th>
                <th style={{ width: 160 }}>Customer</th>
                <th style={{ width: 130 }}>Last seen</th>
                <th style={{ width: 80 }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {(detail.data?.rows ?? []).map((row) => (
                <tr key={`${row.source}-${row.ciId}-${row.discoveredId}-${row.address}`}>
                  <td>
                    {/* Fisa masinii: aparatul din inventar, CI-ul, sau - pentru unul
                        gasit numai de scanare - lista aparatelor descoperite. */}
                    <MachineLink host={row} withFqdn />
                  </td>
                  <td className="adm-muted">{row.serviceName || row.application || '—'}</td>
                  <td>
                    {row.everywhere ? (
                      <span className="adm-pill adm-pill-warn" title="Listening on every interface">
                        all interfaces
                      </span>
                    ) : (
                      row.address
                    )}
                  </td>
                  <td className="adm-muted">{row.companyName || '—'}</td>
                  <td className="adm-muted" style={{ fontSize: 11.5 }}>
                    {row.updated}
                  </td>
                  <td>
                    <SourcePill source={row.source} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </Content>
  )
}
